import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowDown,
  Send,
  SmartToy,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import BookingAgent from "./agent/BookingAgent";
import AIService from "./services/AIService";
import BookingConfirmCard from "./components/BookingConfirmCard";
import MessageBubble from "./components/MessageBubble";
import SurveyModal from "../../components/SurveyModal/SurveyModal";
import QueZoneBookingRepository from "./services/QueZoneBookingRepository";
import ToolExecutor from "./services/ToolExecutor";
import { QUEZONE_SYSTEM_PROMPT, VARIABLE_SYSTEM_PROMPT } from "./agent/bookingPrompt";
import axios from "axios";
import { setAssesmentData } from "../../redux/actions/commonActions";

const AI_CDN_ENDPOINT = "https://cdn.que.zone/ai/custweb-agent"

const quickSuggestions = [
  "I want to book an appointment",
  "What services are available?",
  "What are your timings?",
  "I need help choosing a service",
];

const createMessage = ({ role, content, isLoading = false }) => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
  role,
  content,
  isLoading,
  timestamp: Date.now(),
});

export default function AIChatBotComponent({
  orgRef = "",
  iframeMode = false,
  onBookingComplete = null,
}) {
  const dispatch = useDispatch();
  const queryParams = useSelector(state => state.common.queryParams);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isAITyping, setIsAITyping] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [surveyId, setSurveyId] = useState(null);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [isAwaitingAdditionalInfo, setIsAwaitingAdditionalInfo] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [unread, setUnread] = useState(0);
  const [organizationName, setOrganizationName] = useState("QueZone Assistant");
  const [organization, setOrganization] = useState(null);
  const [systemPrompt, setSystmePrompt] = useState(QUEZONE_SYSTEM_PROMPT);
  const [quickPrompts, setQuickPrompts] = useState(quickSuggestions);

  const [themeConfig, setThemeConfig] = useState(null);

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const conversationMessagesRef = useRef([]);

  const fetchQuickPrompts = async (orgRef) => {
    try {
      const { data } = await axios.get(`${AI_CDN_ENDPOINT}/quick-prompts/${orgRef ?? ''}`);
      if (data) {
        setQuickPrompts(data?.prompts ?? []);
      }
    } catch (error) {
      console.log("[AI ChatBotComponent]: Error fetching quick prompts", error);
    }
  }

  const fetchSystemPrompt = async (orgRef) => {
    try {
      const { data } = await axios.get(`${AI_CDN_ENDPOINT}/system-prompt/${orgRef ?? ''}`);
      if (data) {
        setSystmePrompt(data);
      }
    } catch (error) {
      console.log("[AI ChatBotComponent]: Error fetching quick prompts", error);
    }
  }

  const repository = useMemo(() => (
    new QueZoneBookingRepository({ dispatch, orgRef })
  ), [dispatch, orgRef]);

  const addAssistantMessage = useCallback((content) => {
    const message = createMessage({ role: "assistant", content });
    conversationMessagesRef.current = [
      ...conversationMessagesRef.current,
      {
        role: "assistant",
        content,
      },
    ];
    setMessages((prev) => [...prev, message]);
  }, []);

  const handleAdditionalInfoRequired = useCallback(({ args, result }) => {
    setPendingBookingData(args);
    setSurveyId(result?.surveyId || args?.surveyId || null);
    setShowAdditionalInfoModal(true);
    setIsAwaitingAdditionalInfo(true);
  }, []);

  const toolExecutor = useMemo(() => {
    return new ToolExecutor({
      repository,
      onBookingComplete: (booking) => {
        setConfirmedBooking(booking);
        if (onBookingComplete) onBookingComplete(booking);
      },
      onAdditionalInfoRequired: handleAdditionalInfoRequired,
    });
  }, [repository, onBookingComplete, handleAdditionalInfoRequired]);

  const agent = useMemo(() => {
    const aiService = new AIService({ orgId: organization?.id });
    return new BookingAgent({ aiService, toolExecutor, systemPrompt });
  }, [organization?.id, toolExecutor, systemPrompt]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const org = await repository.getOrganization();
        if (!isMounted) return;

        setOrganization(org);
        const name = org?.name || "QueZone";
        const greeting = `👋 Hi there! I'm the AI booking assistant for **${name}**.\n\nI can help you book an appointment quickly. To get started, could you please tell me your **full name**?`;
        conversationMessagesRef.current = [
          {
            role: "assistant",
            content: greeting,
          },
        ];
        setOrganizationName(name);
        setMessages([
          createMessage({
            role: "assistant",
            content: greeting,
          }),
        ]);
      } catch (error) {
        if (!isMounted) return;
        const errorMessage = "Sorry, I couldn't load this organization right now. Please try again in a moment.";
        conversationMessagesRef.current = [
          {
            role: "assistant",
            content: errorMessage,
          },
        ];
        setMessages([
          createMessage({
            role: "assistant",
            content: errorMessage,
          }),
        ]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [repository]);

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    setUnread(0);
    setShowScrollBtn(false);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (isAtBottom) {
      scrollToBottom();
    } else {
      setShowScrollBtn(true);
      setUnread((n) => n + 1);
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (queryParams?.themeConfig) {
      const parsedThemeConfig = JSON.parse(queryParams?.themeConfig);
      setThemeConfig(parsedThemeConfig);
    }
  }, [queryParams?.themeConfig]);

  useEffect(() => {
    fetchSystemPrompt(orgRef);
    fetchQuickPrompts(orgRef);
  }, [orgRef])

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowScrollBtn(!atBottom);
    if (atBottom) setUnread(0);
  };

  const proceedToBook = useCallback(async (bookingData, additionalInfoFormData) => {
    const requestData = {
      ...(bookingData || pendingBookingData),
      ...(additionalInfoFormData ? { additionalInfoFormData } : {}),
    };

    if (!requestData || !requestData.customerName) {
      return null;
    }

    try {
      const result = await toolExecutor.execute("book_appointment", requestData);
      return result;
    } catch (error) {
      console.error("[AIChatBotComponent] proceedToBook error:", error);
      return null;
    }
  }, [pendingBookingData, toolExecutor]);

  const handleSurveyModalClose = useCallback(() => {
    setShowAdditionalInfoModal(false);
    setIsAwaitingAdditionalInfo(false);
    setPendingBookingData(null);
    setSurveyId(null);
    addAssistantMessage("The additional information form has been closed. If you want to continue the booking, please start again.");
  }, [addAssistantMessage]);

  const handleAdditionalInfoSubmit = async (responseData) => {
    if (!responseData?.data) return;

    const additionalInfoFormData = {
      eventId: null,
      participantId: null,
      questionAnswers: JSON.stringify(responseData.data),
      status: "COMPLETED",
      surveyId,
    };

    setIsAwaitingAdditionalInfo(true);
    setShowAdditionalInfoModal(false);
    await dispatch(setAssesmentData(additionalInfoFormData));

    addAssistantMessage("Thank you for completing the form. I am retrying your booking now.");
    const result = await proceedToBook(pendingBookingData, additionalInfoFormData);

    setIsAwaitingAdditionalInfo(false);

    // Feed the tool response back into the AI conversation so the agent can continue (OTP flow, retries, etc.)
    try {
      conversationMessagesRef.current = [
        ...conversationMessagesRef.current,
        {
          role: "tool",
          tool_call_id: `book_retry_${Date.now()}`,
          name: "book_appointment",
          content: JSON.stringify(result),
        },
      ];

      const aiResp = await agent.respond({ messages: conversationMessagesRef.current });

      if (aiResp?.messages?.length) {
        conversationMessagesRef.current = aiResp.messages;
      }

      // Show AI reply in chat
      if (aiResp?.content) {
        addAssistantMessage(aiResp.content);
      }
    } catch (e) {
      console.error("Error feeding booking retry result to AI", e);
      if (result?.status === "CONFIRMED") {
        addAssistantMessage("Your booking is confirmed. If you want, you can ask me for details or make another booking.");
        setPendingBookingData(null);
        setSurveyId(null);
      } else if (result?.status === "ADDITIONAL_INFO_REQUIRED") {
        addAssistantMessage("It looks like more information is still required. Please complete the new form that is opening.");
      } else if (result?.status === "OTP_REQUIRED") {
        addAssistantMessage("Your booking requires OTP verification. Please complete the verification step to finish the booking.");
      } else if (result?.error) {
        addAssistantMessage(`Sorry, I couldn't complete your booking: ${result.error}`);
      } else {
        addAssistantMessage("Sorry, I couldn't complete your booking. Please try again.");
      }
    }
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || isAITyping || isAwaitingAdditionalInfo) return;

    setInput("");

    const userMsg = createMessage({ role: "user", content: text });
    const loadingMsg = createMessage({ role: "assistant", content: "", isLoading: true });
    const nextMessages = [...messages, userMsg];
    const nextConversationMessages = [
      ...conversationMessagesRef.current,
      {
        role: "user",
        content: text,
      },
    ];

    setMessages([...nextMessages, loadingMsg]);
    setIsAITyping(true);

    try {
      const response = await agent.respond({ messages: nextConversationMessages });
      if (response.messages?.length) {
        conversationMessagesRef.current = response.messages;
      } else {
        conversationMessagesRef.current = [
          ...nextConversationMessages,
          {
            role: "assistant",
            content: response.content,
          },
        ];
      }

      setMessages((prev) => (
        prev.map((msg) => (
          msg.id === loadingMsg.id
            ? { ...msg, content: response.content, isLoading: false }
            : msg
        ))
      ));
    } catch (error) {
      console.log("[Error] Send Message:", error);
      setMessages((prev) => (
        prev.map((msg) => (
          msg.id === loadingMsg.id
            ? { ...msg, content: "Sorry, I couldn't connect to the assistant. Please try again.", isLoading: false }
            : msg
        ))
      ));
    } finally {
      setIsAITyping(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showSuggestions = messages.length <= 1;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: iframeMode ? "100vh" : { xs: "100dvh", sm: 620 },
        width: iframeMode ? "100%" : { xs: "100%", sm: 400 },
        maxWidth: "100%",
        bgcolor: "background.default",
        borderRadius: iframeMode ? 0 : { xs: 0, sm: 3 },
        overflow: "hidden",
        boxShadow: iframeMode ? "none" : "0 8px 40px rgba(0,0,0,0.18)",
        border: iframeMode ? "none" : "1px solid",
        borderColor: "divider",
        mx: "auto",
        fontFamily: "'Inter', sans-serif",
        position: "relative",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: themeConfig?.headerBackground || "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
          color: themeConfig?.headerColor || "white",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Badge
            variant="dot"
            color="success"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ "& .MuiBadge-dot": { border: "2px solid #1e3a5f", width: 10, height: 10, opacity: 0 } }}
          >
            <Avatar
              sx={{ width: 36, height: 36, bgcolor: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.4)" }}
            >
              <SmartToy sx={{ fontSize: 20 }} />
            </Avatar>
          </Badge>
          <Box flex={1}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2, color: "white" }}>
              {organizationName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
              AI Booking Assistant {/* • Online */}
            </Typography>
          </Box>
          <Chip
            label="AI"
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              color: "white",
              fontSize: "0.65rem",
              height: 20,
              fontWeight: 700,
              letterSpacing: "0.05em",
            }}
          />
        </Stack>
      </Box>

      <Divider />

      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          py: 1.5,
          px: 0.5,
          display: "flex",
          flexDirection: "column",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 4 },
        }}
      >
        {messages.map((msg, i) => (
          <MessageBubble key={msg.id} msg={msg} isLast={i === messages.length - 1} />
        ))}

        {showSuggestions && messages.length === 1 && (
          <Fade in timeout={600}>
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="caption" color="text.disabled" display="block" mb={0.75} pl={0.5}>
                Quick options
              </Typography>
              <Stack spacing={0.75}>
                {quickPrompts.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    variant="outlined"
                    size="small"
                    onClick={() => sendMessage(suggestion)}
                    sx={{
                      justifyContent: "flex-start",
                      height: 28,
                      fontSize: "0.72rem",
                      cursor: "pointer",
                      borderColor: "primary.light",
                      color: "primary.main",
                      "&:hover": { bgcolor: "primary.50" },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Fade>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {confirmedBooking && (
        <BookingConfirmCard booking={confirmedBooking} onClose={() => setConfirmedBooking(null)} />
      )}

      {showScrollBtn && (
        <Fade in>
          <Box sx={{ position: "absolute", bottom: 80, right: 16 }}>
            <Badge badgeContent={unread > 0 ? unread : null} color="primary">
              <IconButton
                size="small"
                onClick={() => scrollToBottom()}
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <KeyboardArrowDown fontSize="small" />
              </IconButton>
            </Badge>
          </Box>
        </Fade>
      )}

      <Divider />

      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        {isAITyping && (
          <Stack direction="row" spacing={0.75} alignItems="center" mb={0.75} pl={0.5}>
            <CircularProgress size={10} thickness={5} />
            <Typography variant="caption" color="text.secondary">
              Assistant is typing…
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={
              showAdditionalInfoModal
                ? "Please complete the additional info form to continue..."
                : isAwaitingAdditionalInfo
                  ? "Please wait while I retry your booking..."
                  : "Type your message…"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={isAITyping || isAwaitingAdditionalInfo}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                fontSize: "0.875rem",
                bgcolor: "background.default",
              },
            }}
          />
          <IconButton
            onClick={() => sendMessage()}
            disabled={!input.trim() || isAITyping}
            color="primary"
            sx={{
              bgcolor: input.trim() && !isAITyping ? "primary.main" : "action.disabledBackground",
              color: input.trim() && !isAITyping ? "white" : "text.disabled",
              borderRadius: 2,
              width: 40,
              height: 40,
              flexShrink: 0,
              transition: "all 0.2s",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <Send sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      <SurveyModal
        show={showAdditionalInfoModal}
        onClose={handleSurveyModalClose}
        title="Additional Information Required"
        surveyId={surveyId}
        completeText="CONFIRM BOOKING"
        onComplete={handleAdditionalInfoSubmit}
      />
    </Box>
  );
}
